import { useState } from "react";
import { NavLink, Link } from "react-router-dom";
import { Menu, X, Phone } from "lucide-react";
import { NAV_LINKS, SITE } from "@/lib/siteData";

export default function SiteHeader() {
  const [open, setOpen] = useState(false);

  return (
    <header
      data-testid="site-header"
      className="sticky top-0 z-50 border-b border-[#D1CCC5] bg-[#F9F8F6]/90 backdrop-blur-md"
    >
      {/* Top bar */}
      <div className="hidden bg-[#16302B] text-[#F9F8F6] md:block">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-2 text-xs font-semibold uppercase tracking-[0.18em] md:px-12">
          <span data-testid="header-service-area">{SITE.serviceArea}</span>
          <span data-testid="header-hours">{SITE.hours}</span>
        </div>
      </div>

      {/* Main nav */}
      <div className="mx-auto flex max-w-7xl items-center gap-6 px-6 py-5 md:px-12">
        <Link
          to="/"
          data-testid="logo-link"
          className="flex shrink-0 items-center gap-3 font-display text-base font-black tracking-tight text-[#16302B] md:text-lg"
        >
          <span className="grid h-9 w-9 shrink-0 place-items-center bg-[#16302B] text-[#D95D39]">
            <span className="font-display text-lg font-black">M</span>
          </span>
          <span className="leading-none whitespace-nowrap">
            {SITE.shortName.toUpperCase()}
            <span className="mx-1 text-[#D95D39]">/</span>
            <span className="text-[#5C5C5C]">LAND SERVICES</span>
          </span>
        </Link>

        {/* Desktop nav */}
        <nav className="ml-auto hidden items-center gap-7 lg:flex">
          {NAV_LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `text-sm font-bold uppercase tracking-[0.18em] transition-colors ${
                  isActive ? "text-[#D95D39]" : "text-[#16302B] hover:text-[#D95D39]"
                }`
              }
              end={l.to === "/"}
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="ml-auto flex items-center gap-3 lg:ml-0">
          <a
            href={SITE.phoneHref}
            data-testid="header-phone-link"
            className="hidden items-center gap-2 border border-[#16302B] px-4 py-2 text-sm font-bold tracking-wide text-[#16302B] transition-colors hover:bg-[#16302B] hover:text-white lg:flex"
          >
            <Phone size={14} strokeWidth={2.5} />
            {SITE.phone}
          </a>
          <Link
            to="/contact"
            data-testid="header-cta-button"
            className="hidden bg-[#D95D39] px-5 py-3 text-sm font-bold uppercase tracking-wider text-white transition-colors hover:bg-[#B94B2C] lg:inline-block"
          >
            Free Quote
          </Link>
          <button
            data-testid="mobile-menu-toggle"
            aria-label="Toggle menu"
            onClick={() => setOpen((v) => !v)}
            className="grid h-11 w-11 place-items-center border border-[#16302B] text-[#16302B] lg:hidden"
          >
            {open ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>

      {/* Mobile nav */}
      {open && (
        <div data-testid="mobile-menu" className="border-t border-[#D1CCC5] bg-[#F9F8F6] lg:hidden">
          <nav className="mx-auto flex max-w-7xl flex-col px-6 py-4 md:px-12">
            {NAV_LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                onClick={() => setOpen(false)}
                data-testid={`mobile-nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `border-b border-[#D1CCC5] py-4 text-sm font-bold uppercase tracking-[0.18em] ${
                    isActive ? "text-[#D95D39]" : "text-[#16302B]"
                  }`
                }
                end={l.to === "/"}
              >
                {l.label}
              </NavLink>
            ))}
            <a
              href={SITE.phoneHref}
              data-testid="mobile-header-phone-link"
              className="mt-4 flex items-center justify-center gap-2 bg-[#D95D39] px-4 py-3 text-sm font-bold uppercase tracking-wider text-white"
            >
              <Phone size={14} strokeWidth={2.5} />
              {SITE.phone}
            </a>
          </nav>
        </div>
      )}
    </header>
  );
}
